import { ChevronRight } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { cn } from "../../lib/utils";
import { Separator } from "../ui/separator";

interface FormSectionProps {
	title: string;
	children: React.ReactNode;
	helpText?: string;
	collapsible?: boolean;
	defaultCollapsed?: boolean;
}

export function FormSection({
	title,
	children,
	helpText,
	collapsible = false,
	defaultCollapsed = false,
}: FormSectionProps) {
	const [collapsed, setCollapsed] = useState(defaultCollapsed);

	const toggleCollapsed = () => setCollapsed((c) => !c);

	return (
		<div className="mb-6">
			<div
				className={cn(
					"flex items-center gap-2 py-2",
					collapsible && "cursor-pointer select-none",
				)}
				role={collapsible ? "button" : undefined}
				tabIndex={collapsible ? 0 : undefined}
				aria-expanded={collapsible ? !collapsed : undefined}
				onClick={collapsible ? toggleCollapsed : undefined}
				onKeyDown={
					collapsible
						? (e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									toggleCollapsed();
								}
							}
						: undefined
				}
			>
				{collapsible && (
					<ChevronRight
						size={16}
						className={cn(
							"text-(--color-muted-foreground) transition-transform duration-200",
							!collapsed && "rotate-90",
						)}
					/>
				)}
				<h3 className="font-medium text-(--color-foreground) text-base">
					{title}
				</h3>
				{helpText && (
					<p className="ml-auto text-(--color-muted-foreground) text-xs">
						{helpText}
					</p>
				)}
			</div>
			<Separator className="mb-4" />
			{!collapsed && <div className="space-y-4">{children}</div>}
		</div>
	);
}
